import {
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { hrDepthApi, type PayrollItem } from '@/api/hr-depth';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function PayrollRegisterReportPage() {
  const [runId, setRunId] = useState<number | undefined>(undefined);

  const { data: runs } = useQuery({
    queryKey: ['hr', 'payroll', 'runs'],
    queryFn: () => hrDepthApi.listRuns(),
  });

  const { data: items, isFetching } = useQuery({
    queryKey: ['hr', 'payroll', 'runs', runId, 'items'],
    queryFn: () => hrDepthApi.runItems(runId!),
    enabled: runId != null,
  });

  const run = runs?.find((r) => r.id === runId) ?? null;

  const totals = (items ?? []).reduce(
    (acc, it: PayrollItem) => ({
      gross: acc.gross + Number(it.grossSalary),
      ded:
        acc.ded +
        Number(it.absentDeduction) +
        Number(it.unpaidLeaveDeduction) +
        Number(it.manualDeductions) +
        Number(it.advanceDeduction),
      add: acc.add + Number(it.manualAdditions),
      net: acc.net + Number(it.netSalary),
    }),
    { gross: 0, ded: 0, add: 0, net: 0 },
  );

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Reports"
        title="Payroll register"
        description="Per-month list of every payroll item with deductions, additions, and net. Slip numbers are assigned on finalization."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/payroll-register.xlsx',
              params: runId != null ? { runId } : {},
              fileName: `payroll-register-${runId}.xlsx`,
              disabled: runId == null,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Pick a payroll run"
          style={{ minWidth: 280 }}
          value={runId}
          onChange={(v) => setRunId(v)}
          options={(runs ?? []).map((r) => ({
            value: r.id,
            label: `${dayjs(r.payrollMonth).format('MMMM YYYY')} · ${r.status}${r.reversed ? ' (reversed)' : ''}`,
          }))}
        />
        {run && (
          <Tag color={run.reversed ? 'default' : run.status === 'FINALIZED' ? 'success' : 'processing'}>
            {run.reversed ? 'Reversed' : run.status}
          </Tag>
        )}
      </Space>

      {runId == null ? (
        <Empty description="Pick a run to view its register" />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Staff paid" value={items?.length ?? 0} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Gross" value={PKR(totals.gross)} prefix="PKR" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Total deductions" value={PKR(totals.ded)} prefix="PKR" styles={{ content: { color: '#cf1322' } }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Net" value={PKR(totals.net)} prefix="PKR" styles={{ content: { color: '#3f8600' } }} />
              </Card>
            </Col>
          </Row>

          <Card styles={{ body: { padding: 0 } }}>
            <Table
              rowKey="id"
              size="small"
              loading={isFetching}
              dataSource={items ?? []}
              pagination={{ pageSize: 50 }}
              columns={[
                {
                  title: 'Slip',
                  dataIndex: 'salarySlipNumber',
                  width: 100,
                  render: (n: number | null) =>
                    n ? <Tag color="processing" style={{ fontFamily: 'monospace' }}>#{n}</Tag> : <span style={{ opacity: 0.4 }}>—</span>,
                },
                { title: 'Staff', dataIndex: 'staffName' },
                { title: 'Gross', dataIndex: 'grossSalary', width: 120, align: 'right', render: (v: string) => `PKR ${PKR(v)}` },
                { title: 'Absent', dataIndex: 'absentDeduction', width: 110, align: 'right', render: (v: string) => Number(v) > 0 ? `PKR ${PKR(v)}` : '—' },
                { title: 'Unpaid leave', dataIndex: 'unpaidLeaveDeduction', width: 130, align: 'right', render: (v: string) => Number(v) > 0 ? `PKR ${PKR(v)}` : '—' },
                { title: 'Advance', dataIndex: 'advanceDeduction', width: 110, align: 'right', render: (v: string) => Number(v) > 0 ? `PKR ${PKR(v)}` : '—' },
                { title: 'Manual −', dataIndex: 'manualDeductions', width: 110, align: 'right', render: (v: string) => Number(v) > 0 ? `PKR ${PKR(v)}` : '—' },
                { title: 'Manual +', dataIndex: 'manualAdditions', width: 110, align: 'right', render: (v: string) => Number(v) > 0 ? `PKR ${PKR(v)}` : '—' },
                {
                  title: 'Net',
                  dataIndex: 'netSalary',
                  width: 130,
                  align: 'right',
                  render: (v: string) => <Typography.Text strong>PKR {PKR(v)}</Typography.Text>,
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
